import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { authFetch, useAuth } from "../../App";
import { ArrowLeft, ClipboardCheck, Plus, Check, X, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const API = "/api";

const PROTOCOL_TEMPLATES = [
  {
    key: "erstpruefung",
    label: "Erstpruefung nach DIN VDE 0100-600",
    sections: [
      { title: "Sichtpruefung", items: [
        "Schutzleiterverbindungen korrekt",
        "Isolierung unbeschaedigt",
        "Zugentlastungen vorhanden",
        "Kabelverlegung ordnungsgemaess",
        "Beschriftung vollstaendig",
        "Schutzart eingehalten",
      ]},
      { title: "Messungen", items: [
        "Isolationswiderstand >= 1 MOhm",
        "Schleifenimpedanz geprueft",
        "RCD-Ausloesezeit <= 200ms",
        "Durchgaengigkeit Schutzleiter",
        "Erdungswiderstand <= 2 Ohm",
      ]},
      { title: "Funktionspruefung", items: [
        "Schaltgeraete funktionsfaehig",
        "RCD-Prueftaste loest aus",
        "Drehrichtung korrekt",
        "Notausschaltung funktioniert",
      ]},
    ],
  },
  {
    key: "wiederkehrend",
    label: "Wiederkehrende Pruefung nach DIN VDE 0105-100",
    sections: [
      { title: "Sichtpruefung", items: [
        "Anlage allgemein in Ordnung",
        "Keine sichtbaren Schaeden",
        "Kennzeichnung vorhanden",
        "Warnhinweise angebracht",
      ]},
      { title: "Messungen", items: [
        "Isolationswiderstand gemessen",
        "Schutzleiterwiderstand gemessen",
        "RCD geprueft",
      ]},
    ],
  },
  {
    key: "abnahme",
    label: "Abnahmeprotokoll",
    sections: [
      { title: "Allgemein", items: [
        "Installation gemaess Auftrag",
        "Sauberkeit am Arbeitsplatz",
        "Alle Maengel beseitigt",
        "Dokumentation uebergeben",
        "Kunde eingewiesen",
      ]},
    ],
  },
];

export default function Pruefprotokolle() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { user } = useAuth();
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    loadProtocols();
  }, [projectId]);

  const loadProtocols = async () => {
    try {
      const res = await authFetch(`${API}/projects/${projectId}/protocols`);
      if (res.ok) setProtocols(await res.json());
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const createProtocol = async (template) => {
    setCreating(true);
    try {
      const res = await authFetch(`${API}/projects/${projectId}/protocols`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          template_key: template.key,
          label: template.label,
          sections: template.sections.map(s => ({
            title: s.title,
            items: s.items.map(item => ({ text: item, checked: false, note: "" })),
          })),
        }),
      });
      if (res.ok) {
        toast.success("Pruefprotokoll erstellt");
        loadProtocols();
        setSelectedTemplate(null);
      }
    } catch (e) {
      toast.error("Fehler beim Erstellen");
    }
    setCreating(false);
  };

  const toggleItem = async (protocolId, sectionIdx, itemIdx, checked) => {
    try {
      await authFetch(`${API}/projects/${projectId}/protocols/${protocolId}/toggle`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ section_idx: sectionIdx, item_idx: itemIdx, checked }),
      });
      setProtocols(prev => prev.map(p => {
        if (p.id !== protocolId) return p;
        const updated = { ...p };
        updated.sections = [...p.sections];
        updated.sections[sectionIdx] = { ...p.sections[sectionIdx] };
        updated.sections[sectionIdx].items = [...p.sections[sectionIdx].items];
        updated.sections[sectionIdx].items[itemIdx] = {
          ...p.sections[sectionIdx].items[itemIdx],
          checked,
        };
        return updated;
      }));
    } catch (e) {
      toast.error("Fehler beim Speichern");
    }
  };

  const getProgress = (protocol) => {
    let total = 0, done = 0;
    protocol.sections.forEach(s => s.items.forEach(i => { total++; if (i.checked) done++; }));
    return { total, done, percent: total > 0 ? Math.round((done / total) * 100) : 0 };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f1117] text-white pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0f1117]/95 backdrop-blur border-b border-gray-800 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-white/10 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-green-400" />
              <h1 className="text-base font-semibold">Pruefprotokolle</h1>
            </div>
          </div>
          <Button size="sm" onClick={() => setSelectedTemplate(true)} className="bg-green-600 hover:bg-green-700 gap-1" data-testid="create-protocol-btn">
            <Plus className="w-4 h-4" /> Neu
          </Button>
        </div>
      </div>

      <div className="px-4 pt-4 space-y-3">
        {/* Template selection */}
        {selectedTemplate && (
          <div className="bg-[#161b26] rounded-xl p-4 border border-green-800 space-y-2">
            <p className="text-sm font-medium text-green-400 mb-2">Vorlage waehlen:</p>
            {PROTOCOL_TEMPLATES.map(t => (
              <button
                key={t.key}
                onClick={() => createProtocol(t)}
                disabled={creating}
                className="w-full text-left p-3 bg-[#1a1f2e] rounded-lg hover:bg-[#1e2536] transition-colors text-sm"
                data-testid={`template-${t.key}`}
              >
                {t.label}
              </button>
            ))}
            <button onClick={() => setSelectedTemplate(null)} className="text-xs text-gray-500 hover:text-gray-300 mt-1">
              Abbrechen
            </button>
          </div>
        )}

        {/* Protocol list */}
        {protocols.length === 0 && !selectedTemplate && (
          <div className="text-center py-12 text-gray-500">
            <ClipboardCheck className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Noch keine Pruefprotokolle</p>
          </div>
        )}

        {protocols.map(p => {
          const prog = getProgress(p);
          const expanded = expandedId === p.id;
          return (
            <div key={p.id} className="bg-[#161b26] rounded-xl border border-gray-800 overflow-hidden">
              <button
                onClick={() => setExpandedId(expanded ? null : p.id)}
                className="w-full flex items-center justify-between p-4 text-left"
                data-testid={`protocol-${p.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{p.label}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <div className="flex-1 h-1.5 bg-gray-700 rounded-full max-w-[120px]">
                      <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${prog.percent}%` }} />
                    </div>
                    <span className="text-xs text-gray-400">{prog.done}/{prog.total}</span>
                    {prog.percent === 100 && <Check className="w-4 h-4 text-green-400" />}
                  </div>
                </div>
                {expanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
              </button>

              {expanded && (
                <div className="px-4 pb-4 space-y-4 border-t border-gray-800 pt-3">
                  {p.sections.map((section, si) => (
                    <div key={si}>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{section.title}</p>
                      <div className="space-y-1">
                        {section.items.map((item, ii) => (
                          <button
                            key={ii}
                            onClick={() => toggleItem(p.id, si, ii, !item.checked)}
                            className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                              item.checked ? "bg-green-900/20" : "bg-[#1a1f2e] hover:bg-[#1e2536]"
                            }`}
                            data-testid={`check-${p.id}-${si}-${ii}`}
                          >
                            <div className={`w-5 h-5 rounded flex items-center justify-center border ${
                              item.checked ? "bg-green-600 border-green-600" : "border-gray-600"
                            }`}>
                              {item.checked && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <span className={`text-sm ${item.checked ? "text-gray-400 line-through" : "text-gray-200"}`}>
                              {item.text}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-gray-600">Erstellt: {new Date(p.created_at).toLocaleDateString("de-DE")} von {p.created_by_name}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
